'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, InputNumber, Tooltip, Spinner, Favorite, Skeleton } from '../components';
import clsx from 'clsx';
import { styled } from '@mui/system';
import { checkIfFavorite, updateFavorite } from '@/app/utils/check-favorite';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { syncPrice } from '@/app/utils/price-availability';
import { usePriceStore } from '@/store/price-store';
import { useAppStore } from '@/store/app-store';
import { cartService } from '@/services/cart.service';
import { useDebounce } from 'ahooks';
import { TableColumn } from '../components/Table/use-column';
import { Availability } from '@/types/cart';
import { processNumberString, redirectLoginPage } from '@/app/utils';
import { usePriceFormat } from '@/hooks/use-price-format';
import { SpinnerBox } from './spinner-box';
import ResponsiveTable from './responsive-table';
import { CatalogNotes, ChildCatalogs } from '@/types/product-detail';
import LanguageContext from '@/context/language';
import Link, { LinkProps } from 'next/link';
import { CURRENCY } from '@/app/utils/constant';
import { useCountry } from '@/hooks/use-country';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { usePlacement } from '../tracking/use-tracking-helpers';
import { isLocalSource } from '@/app/utils/check-local-source';
import { pdpInteractions } from '@/shared/tracking/page-load';
import { getContactUsLink } from '@/app/utils/get-contact-us-link';
import { number } from 'mathjs';
import { useRouter } from 'next/navigation';
interface ContextProps {
  tableData: ComponentChildCatalog[];
  setTableData: React.Dispatch<React.SetStateAction<ComponentChildCatalog[]>>;
  noteLoading: boolean;
}

const PricingAvailabilityTableContext = createContext<ContextProps>(null);

interface IProps {
  page?: 'search' | 'pdp';
  bulkQuoteLink: LinkProps['href'];
  className?: string;
  additionalFooter?: JSX.Element;
  childCatalogs: ChildCatalogs[];
  childCatalogDetail: CatalogNotes[];
  showLoading: boolean;
  requestBulkText: string;
  noteLoading: boolean;
  onCartAdd: (products) => Promise<void>;
  getPriceFailSkus?: (skus: string[]) => void;
}

interface ComponentChildCatalog extends Partial<CatalogNotes & Pick<ChildCatalogs, 'webCatalogNumber'>> {
  quantity?: number;
  availability?: Availability;
  isFavorite?: boolean;
}

interface TableItem {
  row: ComponentChildCatalog;
  index?: number;
  favoriteData?: boolean;
}

interface PriceProps extends TableItem {
  field: 'listPrice' | 'bestPrice';
}

const StyledResponsiveTable = styled(ResponsiveTable)`
  width: 100%;
  th {
    font-weight: bold;
    padding: 12px 4px !important;
  }
  td {
    padding: 24px 4px !important;
  }
`;

function PricingAvailabilityTable({
  bulkQuoteLink,
  className,
  additionalFooter,
  childCatalogs,
  childCatalogDetail = [],
  showLoading = false,
  page = 'search',
  requestBulkText,
  noteLoading,
  onCartAdd,
  getPriceFailSkus,
}: IProps) {
  const { isAnonymous } = useAppStore();
  const { t } = useLang(NAMESPACE.common);
  const [tableData, setTableData] = useState<ComponentChildCatalog[]>([]);
  const { productPrice } = usePriceStore();
  const [addCartLoading, setAddCartLoading] = useState(false);
  const [columnData, setColumnData] = useState([]);
  const { trackEventEffect, trackEvent } = useEventTracking();
  const placement = usePlacement();

  useEffect(() => {
    if (childCatalogs?.length > 0) {
      const catalogNumbersArr = childCatalogs?.map((catalog) => catalog.childCatalogNumber);
      const childCatalogsArray = childCatalogs.map((item) => ({
        catalogNumber: item.childCatalogNumber,
        webCatalogNumber: item.webCatalogNumber,
      }));
      setTableData(childCatalogsArray);

      if (!isAnonymous) {
        const catalogNumbersStr = catalogNumbersArr.join(',');
        checkIfFavorite(catalogNumbersStr).then((res) => {
          updateTableData(res);
        });
      }
      syncPrice(childCatalogs).then((res) => {
        getPriceFailSkus(res.failList);
        setTableData((prev) => {
          const productPrice = usePriceStore.getState().productPrice;
          const data = [...prev];
          return data.sort((a, b) => {
            return (
              (!!productPrice[a.catalogNumber]?.listPrice ? productPrice[a.catalogNumber]?.listPrice : Infinity) -
              (!!productPrice[b.catalogNumber]?.listPrice ? productPrice[b.catalogNumber]?.listPrice : Infinity)
            );
          });
        });
      });
    }
  }, [childCatalogs]);

  useEffect(() => {
    if (childCatalogDetail.length > 0) {
      updateTableData(childCatalogDetail);
    }
  }, [childCatalogDetail]);

  const updateTableData = (newData: ComponentChildCatalog[]) => {
    setTableData((prevData) => {
      const updatedData = [...prevData];
      newData.forEach((newItem) => {
        const index = updatedData.findIndex((item) => item.catalogNumber === newItem.catalogNumber);
        updatedData[index] = {
          ...updatedData[index],
          ...newItem,
        };
      });
      return updatedData;
    });
  };

  const onAddCartHandle = async () => {
    setAddCartLoading(true);
    const filterQuantityIsZero = tableData.filter((item) => item.quantity && item.quantity !== 0);
    await onCartAdd(filterQuantityIsZero);
    setAddCartLoading(false);
  };

  const addToCartDisable = useMemo(() => {
    return (
      tableData.reduce((acc, item) => {
        return acc + (item?.quantity || 0);
      }, 0) === 0
    );
  }, [tableData, productPrice]);

  const tableClsx = clsx({
    [className]: !!className,
    'md:hidden sm:hidden w-full': true,
  });
  // track AA
  trackEventEffect(
    {
      key: 'productImpressions',
      action: 'productImpression',
      payload:
        tableData &&
        tableData.map((item) => {
          return {
            catalogId: item.catalogNumber,
            placement,
          };
        }),
    },
    tableData && tableData.length > 0 && page !== 'pdp'
  );

  useEffect(() => {
    const defaultColumns: TableColumn<ComponentChildCatalog>[] = [
      {
        accessorKey: 'catalogNumber',
        header: t('catalog'),
        size: 120,
        cell: (row) => {
          return <span>{row?.webCatalogNumber ? row.webCatalogNumber : row.catalogNumber}</span>;
        },
        style: {
          minWidth: '150px',
        },
      },
      {
        accessorKey: 'unitSize',
        header: page === 'search' ? t('size') : t('unit.size'),
        size: 80,
        cell: (row) => {
          return <Size row={row} />;
        },
      },
      {
        accessorKey: 'listPrice' as any,
        size: 130,
        header: `${t('listPrice')} (${CURRENCY})`,
        cell: (row) => {
          return <Price row={row} field="listPrice" />;
        },
      },
      {
        accessorKey: 'yourPrice' as any,
        size: 151,
        header: () => (
          <div className="flex">
            {t('yourPrice')}({CURRENCY})
            {isAnonymous && (
              <Tooltip className="flex items-center" placement="top" maxWidth={278} title={t('sign.in.price.tooltip')}>
                <div className="icon-16-info-bold ml-1 cursor-pointer text-info" />
              </Tooltip>
            )}
          </div>
        ),
        cell: (row) => {
          return <Price row={row} field="bestPrice" />;
        },
      },
      page === 'search' && {
        accessorKey: 'skuNote',
        header: t('common.note'),
        size: 70,
        cell: (row) => {
          return <Note row={row} />;
        },
      },
      {
        accessorKey: 'availability',
        header: t('stockAvailability'),
        cell: (row) => {
          return <StockAvailability row={row} />;
        },
        style: {
          minWidth: '120px',
        },
      },
      {
        accessorKey: 'quantity',
        header: t('quantity'),
        size: page === 'pdp' ? 170 : 110,
        cell: (row, index) => {
          return <QuantityInputNumber row={row} index={index} />;
        },
      },
      page === 'pdp' && {
        accessorKey: 'skuNote',
        header: t('common.note'),
        cell: (row) => {
          return <NoteText row={row} />;
        },
      },
      {
        enableHiding: isAnonymous,
        accessorKey: 'saveToList' as any,
        header: t('saveToList'),
        size: 110,
        cell: (row, index) => {
          return <SaveToListButton row={row} index={index} key={row.catalogNumber} />;
        },
      },
    ];
    setColumnData(defaultColumns);
  }, []);

  if (showLoading) {
    return (
      <div className="flex justify-center">
        <Spinner isLoading className="my-6" />
      </div>
    );
  }

  return (
    <PricingAvailabilityTableContext.Provider value={{ tableData, setTableData, noteLoading }}>
      <div className="p-4 sm:p-0 md:p-0 sm:pl-9 md:pl-9">
        <StyledResponsiveTable
          size="condensed"
          className={tableClsx}
          columns={columnData.filter(Boolean)}
          dataSource={tableData}
          getRowId={(row: any) => row.catalogNumber}
        />
        <div className="flex md:flex-col sm:flex-col md:gap-y-4 sm:gap-y-4 gap-x-6 mt-6">
          <div className="flex-1">{additionalFooter}</div>
          <Link
            prefetch
            href={bulkQuoteLink}
            className="flex"
            onClick={() => {
              page === 'pdp' &&
                trackEvent({
                  key: 'eventsData',
                  action: 'PDPInteraction',
                  payload: {
                    event_name: 'PDPInteraction',
                    PDP_interaction_type: pdpInteractions['request_quote'],
                  },
                });
            }}
          >
            <Button type="outline" className="flex-1 request-bulk-text">
              {requestBulkText}
            </Button>
          </Link>
          <SpinnerBox isLoading={addCartLoading}>
            <div className="flex">
              <Button
                className="flex-1 add-to-cart"
                type="primary"
                disabled={addToCartDisable}
                onClick={onAddCartHandle}
              >
                {t('add.to.Cart')}
              </Button>
            </div>
          </SpinnerBox>
        </div>
      </div>
    </PricingAvailabilityTableContext.Provider>
  );
}

function StockAvailability({ row }: TableItem) {
  const { setTableData, noteLoading } = useContext(PricingAvailabilityTableContext);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLang(NAMESPACE.common);
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    if (row.quantity !== undefined) {
      cartService
        .getProductsAvailability({
          catalogNumber: row.catalogNumber,
          requestQuantity: row.quantity ? row.quantity : 1,
        })
        .then((response) => {
          setTableData((prevData) => {
            const newData = [...prevData];
            const findItemByCatalogNumber = newData.findIndex((item) => item.catalogNumber === row.catalogNumber);
            newData[findItemByCatalogNumber] = {
              ...newData[findItemByCatalogNumber],
              availability: response,
            };
            return newData;
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [row.quantity, row.catalogNumber]);

  if (loading || noteLoading) {
    return <Skeleton rows={1} horizontal="rtl" />;
  }

  if (!row.availability) {
    return (
      <Button
        type="link"
        className="p-0 text-left flex items-center col-span-2 justify-self-end  h-6  print:hidden"
        onClick={() => {
          trackEvent({
            action: 'login',
          });
          redirectLoginPage();
        }}
      >
        {t('sign.in')}
      </Button>
    );
  }

  return (
    <div className="flex flex-col">
      {row?.availability?.availabilityMessage[lang.toUpperCase()]?.map((message, index) => {
        return (
          <span key={index} className="text-sm font-normal text-gray-1200">
            {message}
          </span>
        );
      })}
    </div>
  );
}

function QuantityInputNumber({ row, index }: TableItem) {
  const { productPrice } = usePriceStore();
  const { lang } = useLang(NAMESPACE.common);
  const { setTableData, noteLoading } = useContext(PricingAvailabilityTableContext);
  const [quantity, setQuantity] = useState<number>(row.quantity || 0);
  const debounceQuantity = useDebounce(quantity, {
    wait: 300,
  });

  useEffect(() => {
    setTableData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        quantity: debounceQuantity,
      };
      return newData;
    });
  }, [debounceQuantity]);

  const price = productPrice?.[row.catalogNumber];

  const inputNumberDisable = useMemo(() => {
    if (!price) return true;
    if (noteLoading) return true;
    if (price.priceStatus !== 'Orderable' || number(price.listPrice ?? 0) === 0) return true;
    if (row.availability && row.availability.availabilityMessage[lang.toUpperCase()].length === 0) return true;
    return row.dwgFlag === 'Y' && row.availability?.totalStock === 0 && !isLocalSource(row.localSource);
  }, [price, row.availability, row.dwgFlag, noteLoading]);

  const onQuantityChange = (value: string) => {
    setQuantity(processNumberString(value));
  };

  const maxValue = useMemo(() => {
    if (row.dwgFlag === 'Y' && row.availability?.totalStock > 0) {
      return row.availability.totalStock.toString();
    }
    return '9999';
  }, [row.dwgFlag, row.availability]);

  return (
    <InputNumber
      className="w-[104px] flex-shrink-0 md:h-8 sm:h-8"
      textAlign="center"
      minValue="0"
      maxValue={maxValue}
      disabled={inputNumberDisable}
      value={quantity}
      onChange={onQuantityChange}
    />
  );
}

function Price({ row, field }: PriceProps) {
  const router = useRouter();
  const { productPrice } = usePriceStore();
  const { isAnonymous } = useAppStore();
  const { t, lang } = useLang(NAMESPACE.common);
  const getFormattedPrice = usePriceFormat();
  const price = productPrice?.[row.catalogNumber];
  const { isKR } = useCountry();
  const { trackEvent } = useEventTracking();
  const contactUs = useMemo(() => {
    if (field === 'listPrice' && (!price?.listPrice || price?.listPrice === 0)) return true;
    if (field === 'bestPrice' && (!price?.bestPrice || price?.bestPrice === 0)) return true;
    if (field === 'listPrice') return false;
    if (!price) return false;
    if (row.entitled === false) {
      return true;
    }
    return (
      price.priceStatus !== 'Orderable' ||
      (row.dwgFlag === 'Y' && row.availability?.totalStock === 0 && !isLocalSource(row.localSource))
    );
  }, [price, row.availability, row.dwgFlag]);

  if (isAnonymous && field === 'bestPrice') {
    return (
      <Button
        type="link"
        className="p-0 text-left flex items-center col-span-2 justify-self-end h-6 print:hidden"
        onClick={() => {
          trackEvent({
            action: 'login',
          });
          redirectLoginPage();
        }}
      >
        {t('sign.in')}
      </Button>
    );
  }

  if (!price) {
    return <Skeleton rows={1} horizontal="rtl" />;
  }
  if (contactUs && field === 'bestPrice' && isKR) {
    return '';
  }

  if (contactUs) {
    return (
      <Button
        className="font-bold  p-0 text-left flex items-center h-auto leading-none cursor-pointer"
        type="link"
        onClick={() => {
          trackEvent({
            key: 'eventsData',
            action: 'requestQuote',
            payload: {
              event_name: 'requestQuote',
              request_quote_sku: row.catalogNumber,
            },
          });
          router.push(getContactUsLink(lang));
        }}
      >
        {t('contact.us')}
      </Button>
    );
  }

  if (field === 'listPrice' && price?.wopPrice) {
    return (
      <div>
        <span>
          {t('price')} : {getFormattedPrice(price[field])}
        </span>
        <div className="flex flex-col text-[11px] leading-normal">
          <span className="font-bold">
            {t('online.offer')}: {getFormattedPrice(price?.wopPrice)}
          </span>
          {price?.wopPriceEndDate && (
            <span className="text-gray-600">
              ({t('ends')}: {price?.wopPriceEndDate})
            </span>
          )}
        </div>
      </div>
    );
  }

  return getFormattedPrice(price[field]);
}

function Size({ row }: TableItem) {
  const { noteLoading } = useContext(PricingAvailabilityTableContext);

  if (noteLoading) {
    return <Skeleton rows={1} horizontal="rtl" />;
  }

  return <span dangerouslySetInnerHTML={{ __html: row.unitSize }} />;
}

function NoteText({ row }: TableItem) {
  const { noteLoading } = useContext(PricingAvailabilityTableContext);
  const { lang } = useContext(LanguageContext);

  if (noteLoading) {
    return <Skeleton rows={1} horizontal="rtl" />;
  }

  return <span dangerouslySetInnerHTML={{ __html: (lang === 'en' ? row.enSkuNote : row.skuNote) ?? '' }}></span>;
}

function Note({ row }: TableItem) {
  const { noteLoading } = useContext(PricingAvailabilityTableContext);
  const { lang } = useContext(LanguageContext);

  if (noteLoading) {
    return <Skeleton rows={1} horizontal="rtl" />;
  }

  const noteText = (lang === 'en' ? row?.enSkuNote : row?.skuNote) || '';
  const tooltipTitle = noteText ? <span dangerouslySetInnerHTML={{ __html: noteText }}></span> : '';

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <i className="icon-24-document-mono cursor-pointer text-info" />
    </Tooltip>
  );
}

function SaveToListButton({ row, index }: TableItem) {
  const { productPrice } = usePriceStore();
  const [showLoader, setLoader] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { t: commonT } = useLang(NAMESPACE.common);
  // tracking event
  const { trackEvent } = useEventTracking();
  const placement = usePlacement();
  const { setTableData } = useContext(PricingAvailabilityTableContext);

  const cls = clsx({
    'text-info cursor-pointer ml-4': true,
    'icon-24-favorites-mono': !row.isFavorite,
    'icon-24-favorites-bold': row.isFavorite,
  });

  const handleSaveToList = () => {
    setLoader(true);
    updateFavorite([row.catalogNumber], row.isFavorite).then((res: any) => {
      if (res.code === '200' && res.message === 'success') {
        setTableData((prevData) => {
          const newData = [...prevData];
          newData[index] = {
            ...newData[index],
            isFavorite: !row.isFavorite,
          };
          return newData;
        });
        setLoader(false);
        setIsOpen(true);
        setTimeout(() => {
          setIsOpen(false);
        }, 5e3);

        if (row.isFavorite) {
          trackEvent({
            key: 'eventsData',
            action: 'removeFromFavoritesList',
            payload: {
              event_name: 'removeFromFavoritesList',
              contentType: 'Product',
              products: [
                {
                  catalogId: row.catalogNumber,
                  placement,
                },
              ],
            },
          });
        } else {
          trackEvent({
            key: 'eventsData',
            action: 'addToFavoritesList',
            payload: {
              event_name: 'addToFavoritesList',
              contentType: 'Product',
              products: [
                {
                  catalogId: row.catalogNumber,
                  quantity: row.quantity,
                  price: productPrice?.[row.catalogNumber]?.bestPrice,
                  placement,
                },
              ],
            },
          });
        }
      }
    });
  };

  return (
    <div className="relative">
      <div className={cls} onClick={handleSaveToList} />
      <div className="absolute">
        <Spinner isLoading={showLoader} />
      </div>
      <Favorite
        isFav={row.isFavorite ?? false}
        isOpen={isOpen}
        title={
          row.isFavorite
            ? commonT('favorite.added').replace('%s', row.catalogNumber)
            : commonT('favorite.removed').replace('%s', row.catalogNumber)
        }
        closeFunc={() => {
          setIsOpen(false);
        }}
      />
    </div>
  );
}

export { PricingAvailabilityTable };
